import { useRef, useState } from "react";
import { addProjectDirectory } from "@/api/spaces";
import { Button } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import {
  useSettingsDraftState,
  useResetSettingsDraft,
} from "./settings-draft-state";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError, SettingsTextField } from "./settings-text-field";
import {
  projectError,
  ProjectPageUnavailable,
  useProjectPage,
} from "./project-page-state";

export function AddFolderPage({ projectId }: { projectId: string }) {
  const state = useProjectPage(projectId);
  if (state.loading || !state.project)
    return (
      <ProjectPageUnavailable loading={state.loading} retry={state.retry} />
    );
  return (
    <AddFolderForm
      key={projectId}
      projectId={projectId}
      paths={state.directories.map((item) => item.path)}
    />
  );
}
function AddFolderForm({
  projectId,
  paths,
}: {
  projectId: string;
  paths: string[];
}) {
  const t = useT(settingsProjectsMessages);
  const nav = useSettingsNav();
  const prefix = `project:${projectId}:add-folder:`;
  const reset = useResetSettingsDraft();
  const [path, setPath] = useSettingsDraftState(`${prefix}path`, "");
  const [label, setLabel] = useSettingsDraftState(`${prefix}label`, "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const writing = useRef(false);
  async function add() {
    if (writing.current) return;
    if (!path.trim()) {
      setError(t("enterFolderPath"));
      return;
    }
    if (paths.includes(path.trim())) {
      setError(t("folderAlreadyAdded"));
      return;
    }
    writing.current = true;
    setSaving(true);
    setError(null);
    try {
      await addProjectDirectory(projectId, {
        path: path.trim(),
        label: label.trim() || undefined,
      });
      reset(prefix);
      nav.open({ page: "projects-project", args: { projectId } });
    } catch (failure) {
      setError(projectError(failure, t("addFolderError")));
    } finally {
      writing.current = false;
      setSaving(false);
    }
  }
  return (
    <form
      data-testid="settings-page-add-folder"
      onSubmit={(event) => {
        event.preventDefault();
        void add();
      }}
    >
      <SettingsTextField
        label={t("folderPath")}
        mono
        testId="add-folder-path"
        value={path}
        onChange={(event) => setPath(event.target.value)}
        disabled={saving}
        autoFocus
        // i18n-ignore: an example path
        placeholder="~/code/my-project"
      />
      <SettingsTextField
        label={t("labelOptional")}
        testId="add-folder-label"
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        disabled={saving}
      />
      <SettingsFieldError error={error} testId="add-folder-error" />
      <Button
        size="sm"
        type="submit"
        disabled={saving || !path.trim()}
        data-testid="add-folder-submit"
      >
        {saving ? t("adding") : t("addFolder")}
      </Button>
    </form>
  );
}
